const MANAGED = "data-document-seo";
const DESCRIPTION_MAX_LENGTH = 200;

export type DocumentSeoHreflang = "ar" | "en";

export function truncateMetaDescription(
  value: string,
  maxLength = DESCRIPTION_MAX_LENGTH,
): string {
  const plain = value.replace(/\s+/g, " ").trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 1).trimEnd()}…`;
}

export function canonicalHref(origin: string, path: string): string {
  return new URL(path || "/", origin).toString();
}

export function hreflangHrefs(
  origin: string,
  path: string,
  defaultLanguage: DocumentSeoHreflang,
): { language: string; href: string }[] {
  const arabic = languageHref(origin, path, "ar", defaultLanguage);
  const english = languageHref(origin, path, "en", defaultLanguage);
  return [
    { language: "ar", href: arabic },
    { language: "en", href: english },
    { language: "x-default", href: languageHref(origin, path, defaultLanguage, defaultLanguage) },
  ];
}

export function languageHref(
  origin: string,
  path: string,
  language: DocumentSeoHreflang,
  defaultLanguage: DocumentSeoHreflang,
): string {
  const url = new URL(path || "/", origin);
  if (language === defaultLanguage) url.searchParams.delete("lang");
  else url.searchParams.set("lang", language);
  return url.toString();
}

export function stringifyJsonLd(payload: unknown): string {
  return JSON.stringify(payload).replace(/</g, "\\u003c");
}

function managedNodes(): Element[] {
  return [...document.head.querySelectorAll(`[${MANAGED}]`)];
}

function upsertElement(tag: string, identity: string): HTMLElement {
  const existing = document.head.querySelector(`[${MANAGED}="${identity}"]`);
  if (existing instanceof HTMLElement) return existing;
  const element = document.createElement(tag);
  element.setAttribute(MANAGED, identity);
  document.head.appendChild(element);
  return element;
}

export type AppliedDocumentSeo = {
  description?: string | null;
  canonicalHref?: string | null;
  robots?: string;
  jsonLd?: unknown;
  hreflang?: { language: string; href: string }[];
};

export function applyDocumentSeo(seo: AppliedDocumentSeo): void {
  if (seo.description) {
    const meta = upsertElement("meta", "description") as HTMLMetaElement;
    meta.setAttribute("name", "description");
    meta.setAttribute("content", truncateMetaDescription(seo.description));
  } else {
    document.head.querySelector(`[${MANAGED}="description"]`)?.remove();
  }

  const robots = upsertElement("meta", "robots") as HTMLMetaElement;
  robots.setAttribute("name", "robots");
  robots.setAttribute("content", seo.robots ?? "index,follow");

  if (seo.canonicalHref) {
    const link = upsertElement("link", "canonical") as HTMLLinkElement;
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", seo.canonicalHref);
  } else {
    document.head.querySelector(`[${MANAGED}="canonical"]`)?.remove();
  }

  document.head
    .querySelectorAll(`[${MANAGED}^="hreflang-"]`)
    .forEach((node) => node.remove());
  for (const entry of seo.hreflang ?? []) {
    const link = upsertElement("link", `hreflang-${entry.language}`) as HTMLLinkElement;
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", entry.language);
    link.setAttribute("href", entry.href);
  }

  if (seo.jsonLd) {
    const script = upsertElement("script", "jsonld") as HTMLScriptElement;
    script.type = "application/ld+json";
    script.textContent = stringifyJsonLd(seo.jsonLd);
  } else {
    document.head.querySelector(`[${MANAGED}="jsonld"]`)?.remove();
  }
}

export function clearDocumentSeo(): void {
  managedNodes().forEach((node) => node.remove());
}
